import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { DataTable } from '@/common/ui/table/data-table'
import { TableCell } from '@/common/ui/table'
import Spinner from '@/common/components/Spinner'
import {
	ArrowDownRight,
	ArrowUpRight,
	TrendingUp,
	DollarSign,
	Receipt,
	ChevronRight,
} from 'lucide-react'

import useQueryGetBillingDetails from '../../api/hooks/useQueryGetBillingDetails'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import {
	promotionColumns,
	formatBillingMonth,
} from '../BillingDetailsSummary/constants'
import ProviderBreakdownTable from '../BillingDetailsSummary/ProviderBreakdownTable'
import type { PromotionBillingBreakdown, CryptoInvoiceType } from '../../type'

interface InvoiceSummaryModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	billingMonth: string | null
	invoiceAmount?: number
	invoiceType: CryptoInvoiceType
}

export default function InvoiceSummaryModal({
	open,
	onOpenChange,
	billingMonth,
	invoiceType,
}: InvoiceSummaryModalProps) {
	const { data: billingData, isLoading } = useQueryGetBillingDetails({
		billingMonth: billingMonth || '',
	})

	// Fetch general settings for monthly platform fee (fallback)
	const { data: generalSettings } = useQueryGetGeneralSetting()
	const monthlyPlatformFee =
		billingData?.monthlyPlatformFee ?? generalSettings?.monthlyPlatformFee ?? 0

	const formatCurrency = (value: number): string => {
		return `฿${value.toLocaleString('th-TH', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`
	}

	const formatTHB = (value: number): string => {
		return `฿${value.toLocaleString('th-TH', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`
	}

	const formatUSD = (value: number): string => {
		return `$${value.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`
	}

	// Calculate the credit charge
	const creditChargeThb = billingData?.totalChargeThb || 0
	const creditChargeUsd = billingData?.totalChargeUsd || 0
	const exchangeRate = billingData?.exchangeRate || 0

	const summaryItems = [
		{
			title: 'อัตราแลกเปลี่ยน (USD/THB)',
			value: `฿${exchangeRate.toFixed(2)}`,
			icon: DollarSign,
			color: 'text-purple-600',
			bgColor: 'bg-purple-50 dark:bg-purple-950/30',
		},
		{
			title: 'ยอดโอนเข้า',
			value: billingData ? formatCurrency(billingData.totalTransferIn) : '-',
			icon: ArrowDownRight,
			color: 'text-green-600',
			bgColor: 'bg-green-50 dark:bg-green-950/30',
		},
		{
			title: 'ยอดโอนออก',
			value: billingData ? formatCurrency(billingData.totalTransferOut) : '-',
			icon: ArrowUpRight,
			color: 'text-red-600',
			bgColor: 'bg-red-50 dark:bg-red-950/30',
		},
		{
			title: 'กินลูกค้า',
			value: billingData ? formatCurrency(billingData.totalProfit) : '-',
			icon: TrendingUp,
			color: 'text-emerald-600',
			bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
		},
	]

	const providerFooter = billingData?.providerBreakdown?.length ? (
		<>
			<TableCell />
			<TableCell className="font-bold">รวมทั้งหมด</TableCell>
			<TableCell />
			<TableCell className="text-right font-bold text-green-600">
				{formatCurrency(billingData.totalTransferIn)}
			</TableCell>
			<TableCell className="text-right font-bold text-red-600">
				{formatCurrency(billingData.totalTransferOut)}
			</TableCell>
			<TableCell className="text-right font-bold text-emerald-600">
				{formatCurrency(billingData.totalProfit)}
			</TableCell>
			<TableCell className="text-right font-bold text-purple-600">
				{formatCurrency(
					billingData.providerBreakdown.reduce(
						(sum, provider) => sum + provider.nonPromotionCharge,
						0,
					),
				)}
			</TableCell>
			<TableCell className="text-right font-bold text-orange-600">
				{formatCurrency(
					billingData.providerBreakdown.reduce(
						(sum, provider) => sum + provider.promotionCharge,
						0,
					),
				)}
			</TableCell>
			<TableCell className="text-right font-bold text-blue-600">
				{formatTHB(billingData.totalChargeThb)}
			</TableCell>
		</>
	) : null

	const promotionFooter = billingData?.promotionBreakdown?.length ? (
		<>
			<TableCell className="font-bold">รวมโปรโมชั่น</TableCell>
			<TableCell />
			<TableCell className="text-right font-bold text-green-600">
				{formatCurrency(billingData.totalPromotionTransferIn || 0)}
			</TableCell>
			<TableCell className="text-right font-bold text-red-600">
				{formatCurrency(billingData.totalPromotionTransferOut || 0)}
			</TableCell>
			<TableCell className="text-right font-bold text-emerald-600">
				{formatCurrency(billingData.totalPromotionProfit || 0)}
			</TableCell>
			<TableCell className="text-right font-bold text-orange-600">
				{formatCurrency(billingData.totalPromotionCharge || 0)}
			</TableCell>
		</>
	) : null

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex h-[90vh] w-[95vw] max-w-md flex-col overflow-y-auto overflow-x-hidden p-3 sm:max-h-[85vh] sm:max-w-lg sm:p-4 md:max-w-2xl md:p-5 lg:max-w-4xl lg:p-6">
				<DialogHeader className="flex-shrink-0 space-y-1 pb-2 sm:space-y-2 sm:pb-3">
					<DialogTitle className="text-sm font-semibold sm:text-base lg:text-lg">
						สรุปบิล -{' '}
						{billingMonth ? formatBillingMonth(billingMonth) : 'ไม่ระบุ'}
					</DialogTitle>
				</DialogHeader>

				<div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
					{isLoading ? (
						<div className="flex h-64 items-center justify-center">
							<Spinner />
						</div>
					) : !billingData ? (
						<div className="py-12 text-center text-muted-foreground">
							ไม่พบข้อมูลสรุป
						</div>
					) : invoiceType === 'MONTHLY_FEE' ? (
						// Monthly Fee - Show only monthly platform fee
						<div className="space-y-3 py-2 sm:space-y-4 sm:py-4">
							<div className="flex flex-col items-stretch gap-3 sm:items-center">
								{/* Monthly Platform Fee */}
								<div className="flex w-full items-center gap-2.5 rounded-lg border-2 border-orange-400 bg-orange-50 px-3 py-2.5 dark:bg-orange-950/30 sm:w-auto sm:gap-3 sm:px-4 sm:py-3">
									<div className="flex-shrink-0 rounded-lg bg-orange-500 p-1.5 sm:p-2">
										<Receipt className="h-4 w-4 text-white sm:h-5 sm:w-5" />
									</div>
									<div className="min-w-0 flex-1 sm:flex-none">
										<p className="text-xs font-medium text-orange-700 dark:text-orange-400">
											ค่ารายเดือน
										</p>
										<div className="flex flex-wrap items-baseline gap-1.5 sm:gap-2">
											<p className="text-lg font-bold text-orange-600 sm:text-xl">
												{formatUSD(monthlyPlatformFee)}
											</p>
											{exchangeRate > 0 && (
												<p className="text-xs text-orange-600">
													≈ ฿
													{(monthlyPlatformFee * exchangeRate).toLocaleString(
														'th-TH',
														{
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														},
													)}
												</p>
											)}
										</div>
									</div>
								</div>
							</div>

							{/* Exchange Rate Info */}
							<div className="flex justify-center">
								<div className="inline-flex items-center gap-1.5 rounded-lg bg-purple-50 px-2.5 py-1.5 dark:bg-purple-950/30 sm:gap-2 sm:px-3 sm:py-2">
									<DollarSign className="h-3.5 w-3.5 text-purple-600 sm:h-4 sm:w-4" />
									<span className="text-xs text-purple-700 dark:text-purple-400 sm:text-sm">
										อัตราแลกเปลี่ยน: ฿{exchangeRate.toFixed(2)}
									</span>
								</div>
							</div>
						</div>
					) : (
						// Company Commission / Manual - Show full billing details
						<div className="space-y-3 py-2 sm:space-y-4 sm:py-4 md:space-y-6">
							{/* Credit Charge */}
							<div className="flex flex-wrap gap-3">
								{/* THB Card */}
								<div className="inline-flex items-center gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 dark:border-blue-800 dark:bg-blue-950/30 sm:px-4 sm:py-3">
									<div className="flex-shrink-0 rounded-lg bg-blue-500 p-2">
										<Receipt className="h-4 w-4 text-white sm:h-5 sm:w-5" />
									</div>
									<div>
										<p className="text-xs text-muted-foreground">
											ยอดเรียกเก็บเครดิต (THB)
										</p>
										<p className="text-base font-semibold text-blue-600 sm:text-lg">
											{billingData ? formatTHB(creditChargeThb) : '-'}
										</p>
									</div>
								</div>

								{/* USD Card */}
								<div className="inline-flex items-center gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 dark:border-blue-800 dark:bg-blue-950/30 sm:px-4 sm:py-3">
									<div className="flex-shrink-0 rounded-lg bg-blue-500 p-2">
										<Receipt className="h-4 w-4 text-white sm:h-5 sm:w-5" />
									</div>
									<div>
										<p className="text-xs text-muted-foreground">
											ยอดเรียกเก็บเครดิต (USD)
										</p>
										<p className="text-base font-semibold text-blue-600 sm:text-lg">
											{billingData ? formatUSD(creditChargeUsd) : '-'}
										</p>
									</div>
								</div>
							</div>

							{/* Summary Stats */}
							<div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4 md:gap-4">
								{summaryItems.map((item) => {
									const Icon = item.icon
									return (
										<div
											key={item.title}
											className="flex flex-col items-center gap-2 rounded-lg border p-2.5 text-center sm:gap-2.5 sm:p-3 md:flex-row md:p-4 md:text-left"
										>
											<div
												className={`flex-shrink-0 rounded-lg p-1.5 sm:p-2 ${item.bgColor}`}
											>
												<Icon
													className={`h-4 w-4 sm:h-5 sm:w-5 ${item.color}`}
												/>
											</div>
											<div className="min-w-0 flex-1">
												<p className="truncate text-[10px] leading-tight text-muted-foreground sm:text-xs md:text-sm">
													{item.title}
												</p>
												<p
													className={`truncate text-xs font-bold sm:text-sm md:text-base lg:text-lg ${item.color}`}
												>
													{item.value}
												</p>
											</div>
										</div>
									)
								})}
							</div>

							{/* Promotion Breakdown Table */}
							<div>
								<div className="mb-2 sm:mb-3">
									<h4 className="text-xs font-semibold sm:text-sm">
										รายละเอียดแบบโปรโมชั่น
									</h4>
									<p className="text-[11px] text-muted-foreground sm:text-xs">
										รายการที่ใช้โปรโมชั่นจะได้รับส่วนลดค่าบริการ
									</p>
								</div>
								<div className="overflow-x-auto [-webkit-overflow-scrolling:touch] sm:overflow-x-visible">
									<div className="min-w-[600px] sm:min-w-0">
										<DataTable
											columns={promotionColumns}
											data={
												(billingData.promotionBreakdown ??
													[]) as PromotionBillingBreakdown[]
											}
											footer={promotionFooter}
										/>
									</div>
								</div>
							</div>

							{/* Provider Breakdown Table */}
							<div>
								<div className="mb-2 sm:mb-3">
									<h4 className="text-xs font-semibold sm:text-sm">
										รายละเอียดตามผู้ให้บริการ (รวมทุกรายการ)
									</h4>
									<p className="text-[11px] text-muted-foreground sm:text-xs">
										ยอดรวมทั้งหมดรวมโปรโมชั่นและไม่รวมโปรโมชั่น
									</p>
								</div>

								{/* Hint for expandable rows */}
								{billingData.providerBreakdown?.some(
									(p) => p.promotions?.length > 0,
								) && (
									<div className="mb-2 flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2 py-1.5 text-xs sm:mb-3 sm:gap-2 sm:px-3 sm:py-2 sm:text-sm">
										<ChevronRight className="h-3 w-3 flex-shrink-0 text-primary sm:h-4 sm:w-4" />
										<span className="text-primary">
											<strong>คลิกลูกศร</strong> หรือ{' '}
											<strong>ป้ายโปรโมชั่น</strong>{' '}
											เพื่อดูรายละเอียดโปรโมชั่นของแต่ละผู้ให้บริการ
										</span>
									</div>
								)}

								<div className="overflow-x-auto [-webkit-overflow-scrolling:touch] sm:overflow-x-visible">
									<div className="min-w-[800px] sm:min-w-0">
										<ProviderBreakdownTable
											data={billingData.providerBreakdown ?? []}
											footer={providerFooter}
										/>
									</div>
								</div>
								<div className="mt-2 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-950/30 dark:text-amber-400 sm:mt-3 sm:p-2.5 sm:text-xs md:p-3 md:text-sm">
									💡 ยอดเรียกเก็บอาจคาดเคลื่อนจาก (กินลูกค้า × อัตราคอมพานี)
									เนื่องจากบางรายการได้รับส่วนลดจากโปรโมชั่น
								</div>
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
